import type { AxiosInstance } from 'axios';
import untypedServer from 'utils/server';
import {
  Claim,
  LiabilityKind,
  NoShowProtection,
  NoShowTemplate,
  OverviewResponse,
  PaymentMethod,
  ReservationProtectionResponse,
} from 'types/noShowProtection';

const server = untypedServer as AxiosInstance;

const base = (restaurantId: string) => `/v03/noShowProtection/${restaurantId}`;

export const fetchMandateMethods = (restaurantId: string) =>
  server
    .get<{ methods: PaymentMethod[] }>(`${base(restaurantId)}/methods`)
    .then(({ data }) => data.methods ?? []);

export const validateTemplates = (
  restaurantId: string,
  templates: NoShowTemplate[]
) => server.post(`${base(restaurantId)}/templates/validate`, { templates });

export const fetchReservationProtection = (
  restaurantId: string,
  reservationId: string
) =>
  server
    .get<ReservationProtectionResponse>(
      `${base(restaurantId)}/res/${reservationId}`
    )
    .then(({ data }) => data);

/**
 * The first pages of the overview, or with `cursor` / `pendingCursor` (from an
 * earlier answer) the next page of the reservations / pending deposits.
 */
export const fetchOverview = (
  restaurantId: string,
  cursors: { cursor?: string; pendingCursor?: string } = {}
) =>
  server
    .get<OverviewResponse>(`${base(restaurantId)}/overview`, {
      params: cursors.cursor || cursors.pendingCursor ? cursors : undefined,
    })
    .then(({ data }) => data);

type ProtectionResult = { protection: NoShowProtection };
type ClaimResult = { claim: Claim };

export const validateStaffProtection = (
  restaurantId: string,
  reservationId: string,
  input: { phone?: string; sendSms?: boolean; waive: boolean }
) => server.post(`${base(restaurantId)}/res/${reservationId}/validate`, input);

export const requestProtection = (
  restaurantId: string,
  reservationId: string,
  sendSms = true
) =>
  server
    .post<ProtectionResult>(
      `${base(restaurantId)}/res/${reservationId}/request`,
      { sendSms }
    )
    .then(({ data }) => data.protection);

/** `requestId` identifies the click: a repeated request of the same click queues no second SMS. */
export const resendProtectionSms = (
  restaurantId: string,
  reservationId: string,
  requestId: string
) =>
  server
    .post<ProtectionResult>(
      `${base(restaurantId)}/res/${reservationId}/resendSms`,
      { requestId }
    )
    .then(({ data }) => data.protection);

export const waiveProtection = (
  restaurantId: string,
  reservationId: string,
  note?: string
) =>
  server
    .post<ProtectionResult>(
      `${base(restaurantId)}/res/${reservationId}/waive`,
      { note }
    )
    .then(({ data }) => data.protection);

export type CreateClaimBody = {
  requestId: string;
  kind: LiabilityKind;
  persons?: number;
  observationId?: string;
  /** Further partial claim on an existing no-show debt. */
  liabilityId?: string;
  onGuestBehalf?: boolean;
  amountCents: number;
  message: string;
};

export const createClaim = (
  restaurantId: string,
  reservationId: string,
  body: CreateClaimBody
) =>
  server
    .post<ClaimResult>(
      `${base(restaurantId)}/res/${reservationId}/claims`,
      body
    )
    .then(({ data }) => data.claim);

const claimAction = <T = ClaimResult>(
  restaurantId: string,
  claimId: string,
  action: 'charge' | 'paymentLink' | 'refund' | 'cancel' | 'reconcile',
  body?: Record<string, unknown>
) =>
  server
    .post<T>(`${base(restaurantId)}/claims/${claimId}/${action}`, body)
    .then(({ data }) => data);

export const retryCharge = (
  restaurantId: string,
  claimId: string,
  attemptId: string
) => claimAction(restaurantId, claimId, 'charge', { attemptId });

export const createPaymentLink = (
  restaurantId: string,
  claimId: string,
  attemptId: string
) =>
  claimAction<{ claim: Claim; checkoutUrl: string | null }>(
    restaurantId,
    claimId,
    'paymentLink',
    { attemptId, sendEmail: true }
  );

export const refundClaim = (
  restaurantId: string,
  claimId: string,
  requestId: string,
  amountCents: number
) => claimAction(restaurantId, claimId, 'refund', { requestId, amountCents });

export const cancelClaim = (restaurantId: string, claimId: string) =>
  claimAction(restaurantId, claimId, 'cancel');

/** `incompleteSearches`: attempts Mollie could not be searched completely for; nothing was decided for them. */
export const reconcileClaim = (restaurantId: string, claimId: string) =>
  claimAction<ClaimResult & { incompleteSearches?: string[] }>(
    restaurantId,
    claimId,
    'reconcile'
  );

/** Gives up an unclear attempt after a complete Mollie check; a payment found is booked instead. */
export const abandonAttempt = (
  restaurantId: string,
  claimId: string,
  attemptId: string
) =>
  server
    .post<ClaimResult & { outcome: 'abandoned' | 'reconciled' }>(
      `${base(restaurantId)}/claims/${claimId}/attempts/${attemptId}/abandon`
    )
    .then(({ data }) => data);
